import React, { useState, useEffect, useRef } from 'react';
import { GripVertical, MoveHorizontal } from 'lucide-react';

/**
 * 可拖动的分隔线组件
 * @param {number} leftWidth - 左侧宽度百分比 (0-100)
 * @param {function} onResize - 宽度变化回调
 * @param {function} onDragStateChange - 拖动状态变化回调
 * @param {number} minWidth - 最小宽度百分比
 * @param {number} maxWidth - 最大宽度百分比
 * @param {number} collapseThreshold - 折叠临界点百分比
 */
function ResizableSplitter({
  leftWidth,
  onResize,
  onDragStateChange,
  minWidth = 20,
  maxWidth = 70,
  collapseThreshold = 5
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      // 使用 requestAnimationFrame 节流，提升性能
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        // 更新鼠标位置（用于显示拖动指示器）
        setMousePosition({ x: e.clientX, y: e.clientY });

        // 获取容器信息
        const container = containerRef.current?.parentElement;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        // 计算新的宽度百分比
        let newWidth = (mouseX / containerWidth) * 100;

        // 拖动时不立即折叠，允许显示任何宽度（包括小于临界点）
        // 限制最小宽度为1%（允许拖到折叠阈值以下），最大为maxWidth
        newWidth = Math.max(1, Math.min(maxWidth, newWidth));
        onResize(newWidth);
      });
    };

    const handleMouseUp = () => {
      // 取消pending的动画帧
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      setIsDragging(false);
      if (onDragStateChange) {
        onDragStateChange(false);
      }

      // 松开鼠标后检查是否需要折叠
      // 只有小于折叠阈值时才自动折叠到0，其他情况保持用户拖动的位置
      const currentWidth = leftWidth;

      if (currentWidth < collapseThreshold) {
        // 平滑折叠到0
        onResize(0);
      }
      // 移除了自动回弹到 minWidth 的逻辑，让用户自由控制宽度
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      // 清理：取消pending的动画帧
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, minWidth, maxWidth, collapseThreshold, onResize, onDragStateChange, leftWidth]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setMousePosition({ x: e.clientX, y: e.clientY });
    if (onDragStateChange) {
      onDragStateChange(true);
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`relative flex items-center justify-center cursor-col-resize select-none transition-colors ${
          isDragging ? 'bg-blue-400' : 'hover:bg-gray-300 bg-gray-200'
        }`}
        style={{ width: '4px' }}
        onMouseDown={handleMouseDown}
      >
        {/* 拖动图标 */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <GripVertical
            size={16}
            className={`${isDragging ? 'text-white' : 'text-gray-500'}`}
          />
        </div>

        {/* 扩展拖动热区 */}
        <div className="absolute inset-y-0 -left-2 -right-2" />
      </div>

      {/* 拖动时跟随鼠标的指示器 - 动态提示 */}
      {isDragging && (
        <div
          className="fixed z-50 pointer-events-none top-0 left-0"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) translate(-50%, -50%)`,
            willChange: 'transform'
          }}
        >
          <div className={`px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 ${
            leftWidth < collapseThreshold
              ? 'bg-orange-500 text-white'
              : 'bg-blue-500 text-white'
          }`}>
            <MoveHorizontal size={18} />
            <span className="text-sm font-medium">
              {leftWidth < collapseThreshold ? '松手自动关闭输入框' : '拖动调整'}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default ResizableSplitter;
